import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  updatedCallback() {
    super.updatedCallback();

    // A morph resets viewport-gated autoplay videos to their un-loaded server state and
    // discards the JS-loaded <video>; re-evaluate each gate so an in-view clip resumes.
    for (const media of this.querySelectorAll('deferred-media[data-autoplay-when-visible]')) {
      if (media instanceof HTMLElement && 'refreshAutoplayGate' in media) {
        /** @type {import('./media').DeferredMedia} */ (media).refreshAutoplayGate();
      }
    }
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    if (event.detail.data.newProduct) {
      this.replaceWith(newMediaGallery);
      return;
    }

    const currentKey = this.dataset.mediaGalleryKey;
    const newKey = newMediaGallery instanceof HTMLElement ? newMediaGallery.dataset.mediaGalleryKey : undefined;

    if (currentKey && newKey && currentKey === newKey) return;

    if (!currentKey || !newKey || !(newMediaGallery instanceof HTMLElement)) {
      this.replaceWith(newMediaGallery);
      return;
    }

    this.#updateMediaGallery(newMediaGallery);
  };

  /**
   * Updates the media gallery without replacing the custom element.
   * @param {HTMLElement} newMediaGallery - The new media gallery source.
   */
  #updateMediaGallery(newMediaGallery) {
    const selectedMediaId = newMediaGallery.dataset.selectedMediaId;

    this.#syncAttributes(newMediaGallery);

    morph(this, newMediaGallery, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;

        return node.dataset.mediaId || node.getAttribute('slide-id') || undefined;
      },
    });

    queueMicrotask(() => {
      this.updatedCallback();
      this.slideshow?.updatedCallback?.();

      if (selectedMediaId) {
        this.slideshow?.select({ id: selectedMediaId }, undefined, { animate: false });
      }
    });
  }

  /**
   * Syncs root attributes from the server-rendered media gallery.
   * @param {HTMLElement} newMediaGallery - The new media gallery source.
   */
  #syncAttributes(newMediaGallery) {
    for (const attribute of Array.from(this.attributes)) {
      if (!newMediaGallery.hasAttribute(attribute.name)) {
        this.removeAttribute(attribute.name);
      }
    }

    for (const attribute of Array.from(newMediaGallery.attributes)) {
      this.setAttribute(attribute.name, attribute.value);
    }
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
